import { Badge, Button, Stack, Table, Tabs } from "@mantine/core";
import { NAV_ICONS } from "../../lib/navCopy";
import { Link } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, useInfinitePage, useTableSort } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { useQueryFilters } from "../../lib/useQueryFilters";
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE_LABELS,
  disputeStatusColor,
  formatDate,
} from "../../lib/format";

const ADMIN_DISPUTE_TABS = [
  { value: "open", label: "Open" },
  { value: "investigating", label: "Investigating" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
] as const;

const ADMIN_DISPUTE_TAB_DEFAULT = ADMIN_DISPUTE_TABS[0].value;
const ADMIN_DISPUTE_FILTER_DEFAULTS = { tab: ADMIN_DISPUTE_TAB_DEFAULT };
const ADMIN_DISPUTE_FILTER_ALLOWED = { tab: ADMIN_DISPUTE_TABS.map((item) => item.value) };
const ADMIN_DISPUTE_FILTER_OPTIONS = { allowed: ADMIN_DISPUTE_FILTER_ALLOWED };

export function AdminDisputesPage() {
  const disputeFilters = useQueryFilters(ADMIN_DISPUTE_FILTER_DEFAULTS, ADMIN_DISPUTE_FILTER_OPTIONS);
  const tab = disputeFilters.values.tab;
  const statusFilter = tab === "closed" ? "closed" : tab === "all" ? undefined : tab;

  const disputesQuery = useInfinitePage(["admin-disputes", statusFilter], (page, pageSize) =>
    adminApi.listDisputes({ status: statusFilter, page, page_size: pageSize }),
  );

  const disputes = disputesQuery.items;
  const { sorted: sortedDisputes, sortKey, direction, toggle } = useTableSort(disputes, {
    type: (r) => DISPUTE_TYPE_LABELS[r.dispute_type] ?? r.dispute_type,
    status: (r) => DISPUTE_STATUS_LABELS[r.status] ?? r.status,
    raisedBy: (r) => r.raised_by_name,
    against: (r) => r.against_name,
    candidateJob: (r) => r.context?.candidate_name ?? r.context?.job_title ?? null,
    opened: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.disputes}
        title="Disputes"
        subtitle="Mediate disputes between recruiters and companies."
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && disputeFilters.set({ tab: value })}>
          <Tabs.List>
            {ADMIN_DISPUTE_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={disputesQuery.isLoading}
        error={
          disputesQuery.isError
            ? disputesQuery.error instanceof Error
              ? disputesQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void disputesQuery.refetch()}
        hasMore={disputesQuery.hasMore}
        loadingMore={disputesQuery.loadingMore}
        onLoadMore={disputesQuery.loadMore}
        isEmpty={disputes.length === 0}
        empty={{
          icon: NAV_ICONS.disputes,
          title: "No disputes in this category",
          description: "Disputes raised on the platform will appear here.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="type" sortKey={sortKey} direction={direction} onSort={toggle}>
                Type
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="raisedBy" sortKey={sortKey} direction={direction} onSort={toggle}>
                Raised by
              </SortableTh>
              <SortableTh columnKey="against" sortKey={sortKey} direction={direction} onSort={toggle}>
                Against
              </SortableTh>
              <SortableTh columnKey="candidateJob" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate / Job
              </SortableTh>
              <SortableTh columnKey="opened" sortKey={sortKey} direction={direction} onSort={toggle}>
                Opened
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedDisputes.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>{DISPUTE_TYPE_LABELS[d.dispute_type] ?? d.dispute_type}</Table.Td>
                <Table.Td>
                  <Badge color={disputeStatusColor(d.status)} variant="light">
                    {DISPUTE_STATUS_LABELS[d.status] ?? d.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{d.raised_by_name ?? "—"}</Table.Td>
                <Table.Td>{d.against_name ?? "—"}</Table.Td>
                <Table.Td>
                  {d.context?.candidate_name ?? "—"} / {d.context?.job_title ?? "—"}
                </Table.Td>
                <Table.Td>{formatDate(d.created_at)}</Table.Td>
                <Table.Td>
                  <Button component={Link} to={`/admin/disputes/${d.id}`} size="xs" variant="light">
                    Mediate
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
